<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import ContextMenu from 'dashboard/components/ui/ContextMenu.vue';
import MenuItem from 'dashboard/components/widgets/conversation/contextMenu/menuItem.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import {
  useSidebarPinnedConversations,
  useSidebarPinMenu,
} from 'dashboard/composables/useSidebarPinnedConversations';

// Manage menu + rename dialog for "pin to left menu" entries. Mounted once in
// Sidebar.vue; every pin leaf opens it through the shared useSidebarPinMenu
// state (kebab click or right-click on the row).
const { t } = useI18n();
const { pins, unpin, rename, move } = useSidebarPinnedConversations();
const { pinMenuState, closePinMenu } = useSidebarPinMenu();

const pinIndex = computed(() =>
  pins.value.findIndex(pin => Number(pin.id) === pinMenuState.value?.id)
);
const pin = computed(() => pins.value[pinIndex.value] || null);

const moveUpOption = computed(() => ({
  label: t('SIDEBAR.PINNED.MOVE_UP'),
  icon: 'chevron-up',
}));
const moveDownOption = computed(() => ({
  label: t('SIDEBAR.PINNED.MOVE_DOWN'),
  icon: 'chevron-down',
}));
const renameOption = computed(() => ({
  label: t('SIDEBAR.PINNED.RENAME'),
  icon: 'edit',
}));
const unpinOption = computed(() => ({
  label: t('SIDEBAR.PINNED.UNPIN'),
  icon: 'dismiss',
}));

const renameDialogRef = ref(null);
const renameValue = ref('');
const renamePinId = ref(null);

const onMove = delta => {
  move(pin.value.id, delta);
  closePinMenu();
};

const onUnpin = () => {
  unpin(pin.value.id);
  closePinMenu();
};

const onRename = () => {
  renamePinId.value = pin.value.id;
  renameValue.value = pin.value.label || '';
  closePinMenu();
  renameDialogRef.value.open();
};

const onRenameConfirm = () => {
  rename(renamePinId.value, renameValue.value);
  renameDialogRef.value.close();
};
</script>

<template>
  <ContextMenu
    v-if="pinMenuState && pin"
    :x="pinMenuState.x"
    :y="pinMenuState.y"
    @close="closePinMenu"
  >
    <div
      class="p-1 rounded-md shadow-xl bg-n-alpha-3/50 backdrop-blur-[100px] outline-1 outline outline-n-weak/50"
    >
      <MenuItem
        v-if="pinIndex > 0"
        :option="moveUpOption"
        variant="icon"
        @click.stop="onMove(-1)"
      />
      <MenuItem
        v-if="pinIndex < pins.length - 1"
        :option="moveDownOption"
        variant="icon"
        @click.stop="onMove(1)"
      />
      <MenuItem :option="renameOption" variant="icon" @click.stop="onRename" />
      <MenuItem :option="unpinOption" variant="icon" @click.stop="onUnpin" />
    </div>
  </ContextMenu>
  <Dialog
    ref="renameDialogRef"
    type="edit"
    :title="t('SIDEBAR.PINNED.RENAME_DIALOG.TITLE')"
    :confirm-button-label="t('SIDEBAR.PINNED.RENAME_DIALOG.CONFIRM')"
    :cancel-button-label="t('SIDEBAR.PINNED.RENAME_DIALOG.CANCEL')"
    width="md"
    @confirm="onRenameConfirm"
  >
    <Input
      v-model="renameValue"
      :placeholder="t('SIDEBAR.PINNED.RENAME_DIALOG.PLACEHOLDER')"
      @keyup.enter="onRenameConfirm"
    />
  </Dialog>
</template>
